'use client';

/**
 * RosterRail — the dashboard-native "Tracked Roster" index for the chain social feed.
 *
 * Visual contract: the TUNER rail in docs/design/intercepts-prototype/intercepts.html
 * (.roster-head / .roster / .rrow). The rail is FLAT — no card box, no pinned footer —
 * and deliberately CONDENSED: 36px rows (rank · avatar · name · handle count), a long
 * ranked list (RAIL_CAP), and a quiet directory link as the list terminator. The HOST
 * column (DashboardSocialView) owns the sticky scrollport that wraps filter + chips +
 * this roster as ONE scrolling unit — exactly the prototype's scroll model, so the
 * rail's tail is always reachable by scrolling the rail itself, never lost behind an
 * endless feed.
 *
 * `horizontal` renders the MOBILE collapse: a single swipeable avatar-chip strip that
 * keeps discovery glanceable while the feed owns the first screen.
 *
 * Gold-only dashboard tokens (no blue/sky/emerald marketing chrome). Reuses the
 * dashboard Avatar / Eyebrow / Mono atoms. Selecting an entity routes through the
 * page's handle-aware `onEntitySelect` (focus-in-place for handle-bearing entities;
 * deep-link to /tags otherwise).
 */

import { useMemo, useState } from 'react';
import Link from 'next/link';
import { Avatar, Eyebrow, Mono } from '@/components/dashboard/atoms';
import { XIcon } from '@/components/icons/SocialIcons';
import type { EntitySearchData } from '@/types/tags';

type Sort = 'active' | 'az';

// Condensed ranked list — the prototype's tuner shows ~22 rows; the rest live
// behind the directory link at the list's end.
const RAIL_CAP = 22;

interface RosterRailProps {
  entities: EntitySearchData[];
  onEntitySelect: (entityId: string, entitySlug: string) => void;
  currentEntityId?: string;
  directoryHref: string;
  /** Mobile collapse: a horizontal swipeable chip strip instead of the vertical rail. */
  horizontal?: boolean;
}

export default function RosterRail({
  entities,
  onEntitySelect,
  currentEntityId,
  directoryHref,
  horizontal = false,
}: RosterRailProps) {
  const [sort, setSort] = useState<Sort>('active');

  // The roster is about TRACKED social accounts — show handle-bearing entities, ranked.
  const tracked = useMemo(
    () => entities.filter((e) => (e.twitterHandles?.length ?? 0) > 0),
    [entities],
  );

  const ranked = useMemo(() => {
    const list = [...tracked];
    if (sort === 'az') list.sort((a, b) => a.name.localeCompare(b.name));
    else list.sort((a, b) => (b.twitterHandles?.length ?? 0) - (a.twitterHandles?.length ?? 0));
    return list;
  }, [tracked, sort]);

  const top = ranked.slice(0, RAIL_CAP);

  // ── Mobile: horizontal chip strip ─────────────────────────────────────────────
  if (horizontal) {
    return (
      <div
        style={{
          display: 'flex',
          gap: 8,
          overflowX: 'auto',
          overflowY: 'hidden',
          padding: '2px 2px 8px',
          // orthogonal X-scroll never traps the vertical page (feed) scroll
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
        }}
        aria-label="Tracked accounts"
      >
        {ranked.map((e) => {
          const active = currentEntityId === e.id;
          return (
            <button
              key={e.id}
              onClick={() => onEntitySelect(e.id, e.slug)}
              style={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                padding: '6px 10px 6px 6px',
                borderRadius: 999,
                cursor: 'pointer',
                background: active ? 'var(--amber-dim)' : 'var(--surface)',
                border: `0.5px solid ${active ? 'var(--amber-line)' : 'var(--divider)'}`,
              }}
            >
              <RosterAvatar entity={e} size={22} />
              <span
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 600,
                  fontSize: 12,
                  whiteSpace: 'nowrap',
                  color: active ? 'var(--amber)' : 'var(--parchment)',
                }}
              >
                {e.name}
              </span>
            </button>
          );
        })}
        <Link
          href={directoryHref}
          style={{
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            borderRadius: 999,
            textDecoration: 'none',
            background: 'var(--amber-dim)',
            border: '0.5px solid var(--amber-line)',
          }}
        >
          <Mono size={11} color="var(--amber)" weight={600}>
            Browse directory ↗
          </Mono>
        </Link>
      </div>
    );
  }

  // ── Desktop: flat condensed tuner roster (host column owns the scrollport) ─────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Roster head — label left, sort toggle as the quiet right-aligned sub. */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
        <Eyebrow color="var(--slate)">Tracked roster</Eyebrow>
        <button
          type="button"
          onClick={() => setSort(sort === 'active' ? 'az' : 'active')}
          title="Toggle roster order"
          aria-label={`Roster order: ${sort === 'active' ? 'most tracked handles first' : 'alphabetical'}. Click to toggle.`}
          style={{
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            padding: 0,
            fontFamily: 'var(--font-mono)',
            fontSize: 9,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: 'var(--slate)',
          }}
        >
          {sort === 'active' ? 'By coverage' : 'A–Z'} ⇅
        </button>
      </div>

      {/* The condensed ranked list — 36px rows, 1px gaps, hover tint (no dividers). */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }} aria-label="Tracked accounts">
        {top.map((e, i) => {
          const active = currentEntityId === e.id;
          return (
            <button
              key={e.id}
              onClick={() => onEntitySelect(e.id, e.slug)}
              className="hover:bg-parchment-faint"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                height: 36,
                padding: '0 8px',
                borderRadius: 5,
                textAlign: 'left',
                cursor: 'pointer',
                background: active ? 'var(--amber-dim)' : 'transparent',
                border: 'none',
                transition: 'background 140ms',
              }}
            >
              <Mono size={9.5} color="var(--slate)" style={{ width: 16, flexShrink: 0, textAlign: 'right' }}>
                {String(i + 1).padStart(2, '0')}
              </Mono>
              <RosterAvatar entity={e} size={20} />
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 600,
                  fontSize: 13,
                  letterSpacing: '-0.01em',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: active ? 'var(--amber)' : 'var(--parchment)',
                }}
              >
                {e.name}
              </span>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  flexShrink: 0,
                  color: active ? 'var(--amber)' : 'var(--slate)',
                }}
              >
                <XIcon className="w-[9px] h-[9px]" />
                <Mono size={10} color={active ? 'var(--amber)' : 'var(--parchment-dim)'}>
                  {e.twitterHandles.length}
                </Mono>
              </span>
            </button>
          );
        })}

        {/* List terminator — the rest of the roster lives in the directory. */}
        <Link
          href={directoryHref}
          className="hover:bg-parchment-faint"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            height: 36,
            padding: '0 8px',
            marginTop: 3,
            borderRadius: 5,
            textDecoration: 'none',
            transition: 'background 140ms',
          }}
        >
          <Mono size={10.5} color="var(--amber)" weight={600}>
            Browse full directory →
          </Mono>
          {tracked.length > RAIL_CAP && (
            <Mono size={9.5} color="var(--slate)">
              +{tracked.length - RAIL_CAP} more
            </Mono>
          )}
        </Link>
      </div>
    </div>
  );
}

/**
 * Roster avatar — the entity's primary image when present, else the dashboard initials tile.
 * Square 4px-radius to match the dashboard Avatar atom.
 */
function RosterAvatar({ entity, size }: { entity: EntitySearchData; size: number }) {
  if (entity.primaryImage) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={entity.primaryImage}
        alt=""
        loading="lazy"
        style={{
          width: size,
          height: size,
          borderRadius: 4,
          flexShrink: 0,
          objectFit: 'cover',
          border: '0.5px solid var(--divider)',
        }}
      />
    );
  }
  return <Avatar name={entity.name} type={entity.entityType} size={size} />;
}
